// LandlordSetupWizard — 5-step onboarding for a fresh landlord tenant.
// Guards `/admin` (in industry=landlord tenants): AdminHome redirects here
// until landlord_setup_completed_at is set. The wizard captures portfolio
// meta → first rental → Philly compliance lookup → 3 vendors → rent
// reminders / TOS. Progress is server-side (setup/status) so refreshes
// don't lose place.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import { toast } from "sonner";
import {
  House, Buildings, Wrench, ShieldCheck, PaperPlaneTilt, ArrowRight,
  ArrowLeft, CheckCircle, Warning, X, Plus, Trash, MapPin, Phone,
  Sparkle, Copy as CopyIcon,
} from "@phosphor-icons/react";

const dollars = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

const STEPS = [
  { key: "portfolio", label: "Your portfolio", icon: Buildings },
  { key: "rental",    label: "First rental",   icon: House },
  { key: "philly",    label: "Compliance",     icon: ShieldCheck },
  { key: "vendors",   label: "Vendor book",    icon: Wrench },
  { key: "finish",    label: "Rent reminders", icon: PaperPlaneTilt },
];

export default function LandlordSetupWizard() {
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadStatus = async (autoJump = false) => {
    try {
      const r = await api.get("/landlord-auto/setup/status");
      setStatus(r.data);
      if (r.data.completed) { nav("/admin"); return; }
      // Only auto-jump to the first incomplete step on initial mount —
      // never after goNext(), otherwise "no L&I match" bounces the user
      // back to the compliance step forever.
      if (autoJump) {
        if (!r.data.has_portfolio) setIdx(0);
        else if (!r.data.has_rentals) setIdx(1);
        else if (r.data.in_philly_market && !r.data.has_philly_lookup) setIdx(2);
        else if (!r.data.has_vendors) setIdx(3);
        else setIdx(4);
      }
    } catch { toast.error("Could not load setup"); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadStatus(true); }, []);

  const goNext = async () => {
    if (idx < STEPS.length - 1) setIdx(idx + 1);
    await loadStatus(false);
  };
  const goPrev = () => idx > 0 && setIdx(idx - 1);
  const finish = async () => {
    try {
      // Persist the Landlord TOS attestation from Step 5 so we can bypass
      // the global TosGate on the wizard route without losing the record.
      try { await api.post("/landlord-auto/tos-attest", { version: "v1" }); }
      catch { /* non-fatal — user can re-agree via the global gate later */ }
      await api.post("/landlord-auto/setup/complete");
      toast.success("You're all set — welcome to Landlord Autopilot");
      nav("/admin");
    } catch { toast.error("Could not finish setup"); }
  };

  if (loading || !status) {
    return <AdminShell><div className="p-8 text-slate-500 text-sm">Loading setup…</div></AdminShell>;
  }

  const StepIcon = STEPS[idx].icon;
  return (
    <AdminShell>
      <div className="p-4 sm:p-6 max-w-3xl mx-auto" data-testid="landlord-setup-wizard">
        {/* Hero */}
        <div className="mb-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-sky-600">Landlord Autopilot · Setup</div>
          <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tight mt-1" data-testid="wizard-heading">
            Five quick steps and you're collecting rent.
          </h1>
          <div className="text-xs sm:text-sm text-slate-500 font-medium mt-1">
            About 3 minutes. You can add more rentals, vendors, and compliance docs from the dashboard later.
          </div>
        </div>

        {/* Progress ribbon */}
        <div className="flex items-center gap-1.5 mb-4 overflow-x-auto pb-1" data-testid="wizard-progress">
          {STEPS.map((s, i) => (
            <button key={s.key} type="button"
              disabled={i > idx && !isStepUnlocked(i, status)}
              onClick={() => (i <= idx || isStepUnlocked(i, status)) && setIdx(i)}
              data-testid={`wizard-step-${s.key}`}
              className={`h-8 px-2.5 rounded-md text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 whitespace-nowrap transition disabled:opacity-40 disabled:cursor-not-allowed ${
                i === idx ? "bg-sky-600 text-white"
                : i < idx ? "bg-emerald-100 text-emerald-800"
                : "bg-slate-100 text-slate-500"}`}>
              <span className={`h-4 w-4 rounded-full flex items-center justify-center text-[9px] ${
                i === idx ? "bg-white text-sky-600"
                : i < idx ? "bg-emerald-600 text-white"
                : "bg-slate-300 text-slate-600"}`}>
                {i < idx ? <CheckCircle weight="fill" size={10}/> : i + 1}
              </span>
              {s.label}
            </button>
          ))}
        </div>

        {/* Step card */}
        <div className="bg-white border-2 border-slate-200 rounded-xl p-4 sm:p-6" data-testid={`wizard-panel-${STEPS[idx].key}`}>
          <div className="flex items-center gap-2 mb-4">
            <div className="h-10 w-10 rounded-xl bg-sky-600/15 text-sky-600 flex items-center justify-center">
              <StepIcon weight="fill" size={20}/>
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Step {idx + 1} of {STEPS.length}</div>
              <div className="font-display font-black text-lg tracking-tight">{STEPS[idx].label}</div>
            </div>
          </div>

          {idx === 0 && <StepPortfolio status={status} onDone={goNext}/>}
          {idx === 1 && <StepRental status={status} onDone={goNext}/>}
          {idx === 2 && <StepPhilly status={status} onDone={goNext} onSkip={() => setIdx(3)}/>}
          {idx === 3 && <StepVendors status={status} onDone={goNext}/>}
          {idx === 4 && <StepFinish status={status} onDone={finish}/>}

          {/* Foot nav */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
            <button type="button" onClick={goPrev} disabled={idx === 0}
              data-testid="wizard-back"
              className="h-9 px-3 rounded-lg border-2 border-slate-200 hover:border-slate-900 text-slate-800 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
              <ArrowLeft size={12}/> Back
            </button>
            <button type="button"
              onClick={async () => {
                try { await api.post("/landlord-auto/setup/complete"); nav("/admin"); }
                catch { /* ignore */ }
              }}
              data-testid="wizard-skip-all"
              className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-700">
              I'll do this later
            </button>
          </div>
        </div>
      </div>
    </AdminShell>
  );
}

function isStepUnlocked(i, status) {
  if (!status) return false;
  if (i === 0) return true;
  if (i === 1) return status.has_portfolio;
  if (i === 2) return status.has_rentals;
  if (i === 3) return status.has_rentals;
  if (i === 4) return status.has_rentals;
  return false;
}

// ═══════════════════════════════════════════════════════════════════
// STEP 1 — Portfolio meta
// ═══════════════════════════════════════════════════════════════════
function StepPortfolio({ status, onDone }) {
  const meta = status.portfolio_meta || {};
  const [businessName, setBusinessName] = useState(meta.business_name || "");
  const [city, setCity] = useState(meta.market_city || "");
  const [state, setState] = useState(meta.market_state || "PA");
  const [size, setSize] = useState(meta.portfolio_size || "1-4");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!city.trim()) { toast.error("Add your primary city"); return; }
    setSaving(true);
    try {
      await api.post("/landlord-auto/setup/portfolio", {
        business_name: businessName || null,
        market_city: city.trim(),
        market_state: state,
        portfolio_size: size,
      });
      toast.success("Saved");
      await onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="step-portfolio">
      <div className="text-xs text-slate-600 leading-relaxed">
        Tell us where you own. If you're in Philadelphia, we'll auto-check your L&I rental license against public records in Step 3.
      </div>
      <Field label="Business name (optional)">
        <input type="text" value={businessName} onChange={e => setBusinessName(e.target.value)}
          placeholder="e.g. Kensington Property Group LLC"
          data-testid="portfolio-business"
          className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
      </Field>
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <Field label="Primary city">
            <input type="text" value={city} onChange={e => setCity(e.target.value)}
              placeholder="Philadelphia"
              data-testid="portfolio-city"
              className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
          </Field>
        </div>
        <Field label="State">
          <select value={state} onChange={e => setState(e.target.value)}
            data-testid="portfolio-state"
            className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium">
            {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
      </div>
      <Field label="How many rentals do you own?">
        <div className="grid grid-cols-3 gap-2">
          {["1-4", "5-10", "10+"].map(v => (
            <button key={v} type="button" onClick={() => setSize(v)}
              data-testid={`portfolio-size-${v}`}
              className={`h-11 rounded-lg border-2 text-sm font-black transition ${
                size === v ? "border-sky-600 bg-sky-50 text-sky-800" : "border-slate-200 hover:border-slate-400 text-slate-700"}`}>
              {v}
            </button>
          ))}
        </div>
      </Field>
      <button type="button" onClick={save} disabled={saving}
        data-testid="portfolio-save"
        className="w-full h-11 rounded-lg bg-sky-600 hover:bg-sky-700 text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
        {saving ? "Saving…" : (<>Continue <ArrowRight size={12}/></>)}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// STEP 2 — First rental
// ═══════════════════════════════════════════════════════════════════
function StepRental({ status, onDone }) {
  const [rentals, setRentals] = useState([]);
  const [form, setForm] = useState({
    unit_label: "",
    address: "",
    renter_name: "",
    renter_phone: "",
    rent_monthly_cents: "",
    rent_due_day: 1,
  });
  const [saving, setSaving] = useState(false);
  const meta = status.portfolio_meta || {};

  // Prefill the city+state onto the address to reduce typing
  const cityPrefill = useMemo(() => {
    if (form.address || !meta.market_city) return null;
    return `${meta.market_city}, ${meta.market_state || "PA"}`;
  }, [form.address, meta]);

  const loadRentals = async () => {
    try { const r = await api.get("/landlord-auto/rentals"); setRentals(r.data.rentals || []); }
    catch { /* ignore */ }
  };
  useEffect(() => { loadRentals(); }, []);

  const save = async () => {
    if (!form.unit_label.trim()) { toast.error("Nickname the unit"); return; }
    if (!form.address.trim()) { toast.error("Add the address"); return; }
    setSaving(true);
    try {
      const body = { ...form };
      body.rent_monthly_cents = form.rent_monthly_cents === "" ? 0 : Math.round(Number(form.rent_monthly_cents) * 100);
      body.rent_due_day = Number(form.rent_due_day) || 1;
      if (!body.renter_name) delete body.renter_name;
      if (!body.renter_phone) delete body.renter_phone;
      await api.post("/landlord-auto/rentals", body);
      toast.success("Rental added");
      setForm({ unit_label: "", address: "", renter_name: "", renter_phone: "", rent_monthly_cents: "", rent_due_day: 1 });
      await loadRentals();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="step-rental">
      <div className="text-xs text-slate-600 leading-relaxed">
        You only need one to keep going. Add the rest from the dashboard later — each gets its own magic-link renter portal.
      </div>
      {rentals.length > 0 && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 space-y-1" data-testid="rental-added-list">
          <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700">
            You've added {rentals.length} rental{rentals.length === 1 ? "" : "s"}
          </div>
          {rentals.slice(0, 3).map(r => (
            <div key={r.id} className="text-xs text-slate-700 truncate">
              <CheckCircle weight="fill" className="inline text-emerald-600 -mt-0.5 mr-1" size={12}/>
              <span className="font-black">{r.unit_label}</span> — {r.address} · {dollars(r.rent_monthly_cents)}/mo
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Nickname">
          <input type="text" value={form.unit_label} onChange={e => setForm({ ...form, unit_label: e.target.value })}
            placeholder="Unit 2B / 214 Chestnut"
            data-testid="rental-unit-label"
            className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
        </Field>
        <Field label="Rent / month ($)">
          <input type="number" step="1" value={form.rent_monthly_cents} onChange={e => setForm({ ...form, rent_monthly_cents: e.target.value })}
            placeholder="1800"
            data-testid="rental-rent"
            className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
        </Field>
      </div>
      <Field label="Address">
        <input type="text" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
          placeholder="214 Chestnut St, Philadelphia PA"
          data-testid="rental-address"
          className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
        {cityPrefill && (
          <button type="button" onClick={() => setForm({ ...form, address: form.address ? form.address : cityPrefill })}
            className="mt-1 text-[10px] font-black uppercase tracking-widest text-sky-600 hover:text-sky-800 inline-flex items-center gap-1">
            <MapPin size={10}/> Prefill "{cityPrefill}"
          </button>
        )}
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Renter name (optional)">
          <input type="text" value={form.renter_name} onChange={e => setForm({ ...form, renter_name: e.target.value })}
            data-testid="rental-renter-name"
            className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
        </Field>
        <Field label="Renter phone (optional)">
          <input type="tel" value={form.renter_phone} onChange={e => setForm({ ...form, renter_phone: e.target.value })}
            placeholder="215-555-0100"
            data-testid="rental-renter-phone"
            className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <button type="button" onClick={save} disabled={saving}
          data-testid="rental-save"
          className="h-11 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
          <Plus weight="bold" size={12}/> {saving ? "Adding…" : "Add rental"}
        </button>
        <button type="button" onClick={onDone} disabled={rentals.length === 0}
          data-testid="rental-continue"
          className="h-11 rounded-lg bg-sky-600 hover:bg-sky-700 text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
          Continue <ArrowRight size={12}/>
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// STEP 3 — Philly compliance lookup
// ═══════════════════════════════════════════════════════════════════
function StepPhilly({ status, onDone, onSkip }) {
  const [rentals, setRentals] = useState([]);
  const [pickedRid, setPickedRid] = useState("");
  const [candidates, setCandidates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const inPhilly = status.in_philly_market;

  const loadRentals = async () => {
    try {
      const r = await api.get("/landlord-auto/rentals");
      const list = r.data.rentals || [];
      setRentals(list);
      if (list.length && !pickedRid) setPickedRid(list[0].id);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadRentals(); }, []);

  const runLookup = async () => {
    if (!pickedRid) return;
    setLoading(true); setCandidates(null);
    try {
      const r = await api.post(`/landlord-auto/rentals/${pickedRid}/compliance/philly-lookup`);
      setCandidates(r.data);
      const c = r.data.candidates || [];
      if (c.length === 0) toast.info("No matching Philly license found for that address");
      else if (c.length === 1) toast.success("Match found — saved to the rental");
      else toast.info(`${c.length} candidates found — pick the right one below`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Lookup failed")); }
    finally { setLoading(false); }
  };

  const pick = async (license_number) => {
    try {
      await api.post(`/landlord-auto/rentals/${pickedRid}/compliance/philly-lookup/pick`, { license_number });
      toast.success("Saved to rental");
      setSavedCount(c => c + 1);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
  };

  if (!inPhilly) {
    return (
      <div className="space-y-3" data-testid="step-philly-skip">
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
          <div className="text-sm font-black text-slate-800 mb-1">Not in Philadelphia — skipping the license check.</div>
          <div className="text-xs text-slate-600 leading-relaxed">
            Right now we only auto-verify against Philadelphia's L&I Open Data. For other markets, you can upload compliance docs (business license, C of O, lead certs) from the dashboard after setup.
          </div>
        </div>
        <button type="button" onClick={onSkip} data-testid="philly-skip-continue"
          className="w-full h-11 rounded-lg bg-sky-600 hover:bg-sky-700 text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-1.5">
          Continue <ArrowRight size={12}/>
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="step-philly">
      <div className="text-xs text-slate-600 leading-relaxed">
        We'll query the Philadelphia L&I Business License public dataset for a rental license matching your address. If we find exactly one active license, we save it automatically. Multiple matches → you pick.
      </div>
      <Field label="Pick a rental to check">
        <select value={pickedRid} onChange={e => setPickedRid(e.target.value)}
          data-testid="philly-rental-select"
          className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium">
          {rentals.map(r => <option key={r.id} value={r.id}>{r.unit_label} — {r.address}</option>)}
        </select>
      </Field>
      <button type="button" onClick={runLookup} disabled={loading || !pickedRid}
        data-testid="philly-run-lookup"
        className="w-full h-11 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
        <ShieldCheck weight="fill" size={14}/> {loading ? "Checking L&I public records…" : "Auto-verify against Philly L&I"}
      </button>
      {candidates && (
        <div className="rounded-lg border-2 border-slate-200 divide-y divide-slate-100" data-testid="philly-candidates">
          {(candidates.candidates || []).length === 0 && (
            <div className="p-4 text-sm text-slate-600">
              No rental license found at this address. This can mean the license hasn't been issued yet, is under a slightly different address, or is in a different name. You can upload a copy from the dashboard.
            </div>
          )}
          {(candidates.candidates || []).map(c => (
            <div key={c.license_number} className="p-3 flex items-start gap-3" data-testid={`philly-cand-${c.license_number}`}>
              <ShieldCheck weight="fill" size={16} className="text-emerald-600 shrink-0 mt-0.5"/>
              <div className="flex-1 min-w-0">
                <div className="font-black text-sm">#{c.license_number} — {c.owner_name || "—"}</div>
                <div className="text-[11px] text-slate-500">{c.address}</div>
                <div className="text-[11px] text-slate-600">
                  <span className="font-mono">{c.license_type}</span>
                  {c.status && <span className="ml-2">· {c.status}</span>}
                  {c.expires && <span className="ml-2">· expires {c.expires}</span>}
                </div>
              </div>
              <button type="button" onClick={() => pick(c.license_number)}
                data-testid={`philly-pick-${c.license_number}`}
                className="h-8 px-2 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] font-black uppercase tracking-widest">
                Save
              </button>
            </div>
          ))}
        </div>
      )}
      <button type="button" onClick={onDone} data-testid="philly-continue"
        className="w-full h-11 rounded-lg bg-sky-600 hover:bg-sky-700 text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-1.5">
        Continue <ArrowRight size={12}/>
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// STEP 4 — Vendors
// ═══════════════════════════════════════════════════════════════════
function StepVendors({ status, onDone }) {
  const [trades, setTrades] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", trades: ["plumbing"] });
  const [saving, setSaving] = useState(false);
  const inPhilly = status.in_philly_market;

  const load = async () => {
    try {
      const [t, v] = await Promise.all([
        api.get("/landlord-auto/vendors/trades"),
        api.get("/landlord-auto/vendors"),
      ]);
      setTrades(t.data.trades || []);
      setVendors(v.data.vendors || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const toggleTrade = (t) => {
    setForm(f => ({
      ...f,
      trades: f.trades.includes(t) ? f.trades.filter(x => x !== t) : [...f.trades, t],
    }));
  };
  const save = async () => {
    if (!form.name.trim() || !form.phone.trim()) { toast.error("Add a name and phone"); return; }
    setSaving(true);
    try {
      await api.post("/landlord-auto/vendors", form);
      toast.success("Vendor added");
      setForm({ name: "", phone: "", trades: ["plumbing"] });
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    finally { setSaving(false); }
  };
  const removeVendor = async (vid) => {
    try { await api.delete(`/landlord-auto/vendors/${vid}`); await load(); }
    catch { toast.error("Delete failed"); }
  };
  const tapFillPhilly = (name, phone, trades) => setForm({ name, phone, trades });

  return (
    <div className="space-y-3" data-testid="step-vendors">
      <div className="text-xs text-slate-600 leading-relaxed">
        Add 2-3 trusted trades. When a renter reports a repair, our AI triage picks the right one and drafts an SMS to dispatch — you just tap Send.
      </div>

      {inPhilly && (
        <div className="rounded-lg bg-sky-50 border border-sky-200 p-3 space-y-1.5" data-testid="philly-tap-fill">
          <div className="text-[10px] font-black uppercase tracking-widest text-sky-700">Philadelphia — tap to fill</div>
          <div className="flex gap-2 flex-wrap">
            <button type="button" onClick={() => tapFillPhilly("ProCall Property Services", "215-555-0135", ["plumbing", "hvac", "handyman"])}
              data-testid="philly-vendor-procall"
              className="text-[10px] font-black uppercase tracking-widest bg-white border border-sky-300 hover:bg-sky-100 text-sky-800 px-2 h-8 rounded">
              + ProCall (plumb / HVAC / handyman)
            </button>
            <button type="button" onClick={() => tapFillPhilly("Lianni's Crew (LCV)", "267-555-0180", ["cleaning", "handyman", "landscaping"])}
              data-testid="philly-vendor-lcv"
              className="text-[10px] font-black uppercase tracking-widest bg-white border border-sky-300 hover:bg-sky-100 text-sky-800 px-2 h-8 rounded">
              + LCV (cleaning / handyman)
            </button>
          </div>
        </div>
      )}

      {vendors.length > 0 && (
        <div className="rounded-lg border-2 border-slate-200 divide-y divide-slate-100" data-testid="vendors-list">
          {vendors.map(v => (
            <div key={v.id} className="p-3 flex items-center gap-3" data-testid={`vendor-row-${v.id}`}>
              <div className="h-8 w-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <Wrench weight="fill" size={14}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-black text-sm truncate">{v.name}</div>
                <div className="text-[11px] text-slate-500 flex items-center gap-1"><Phone size={10}/> {v.phone}</div>
                {v.trades.length > 0 && (
                  <div className="mt-1 flex items-center gap-1 flex-wrap">
                    {v.trades.map(t => <span key={t} className="text-[9px] font-black uppercase tracking-widest bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{t}</span>)}
                  </div>
                )}
              </div>
              <button type="button" onClick={() => removeVendor(v.id)}
                data-testid={`vendor-remove-${v.id}`}
                className="h-8 w-8 rounded-md bg-white border-2 border-slate-200 hover:border-red-500 text-slate-500 hover:text-red-500 inline-flex items-center justify-center">
                <Trash size={12}/>
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-lg border-2 border-slate-200 p-3 space-y-3">
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Add vendor</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name">
            <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              data-testid="vendor-name"
              placeholder="Ace Plumbing"
              className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
          </Field>
          <Field label="Phone">
            <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
              data-testid="vendor-phone"
              placeholder="215-555-0100"
              className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
          </Field>
        </div>
        <Field label="Trades">
          <div className="flex flex-wrap gap-1.5">
            {trades.map(t => (
              <button key={t} type="button" onClick={() => toggleTrade(t)}
                data-testid={`vendor-trade-${t}`}
                className={`h-8 px-2 rounded-md text-[10px] font-black uppercase tracking-widest border-2 transition ${
                  form.trades.includes(t) ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200 hover:border-slate-400 text-slate-700"}`}>
                {t}
              </button>
            ))}
          </div>
        </Field>
        <button type="button" onClick={save} disabled={saving}
          data-testid="vendor-save"
          className="w-full h-11 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
          <Plus weight="bold" size={12}/> {saving ? "Adding…" : "Add vendor"}
        </button>
      </div>

      <button type="button" onClick={onDone} disabled={vendors.length === 0}
        data-testid="vendors-continue"
        className="w-full h-11 rounded-lg bg-sky-600 hover:bg-sky-700 text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
        Continue <ArrowRight size={12}/>
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// STEP 5 — Rent reminders + finish
// ═══════════════════════════════════════════════════════════════════
function StepFinish({ status, onDone }) {
  const [rentals, setRentals] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [agreed, setAgreed] = useState(false);

  const load = async () => {
    try { const r = await api.get("/landlord-auto/rentals"); setRentals(r.data.rentals || []); }
    catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const toggleReminder = async (r) => {
    setSavingId(r.id);
    try {
      await api.patch(`/landlord-auto/rentals/${r.id}`, { rent_reminder_enabled: !r.rent_reminder_enabled });
      await load();
    } catch { toast.error("Could not update"); }
    finally { setSavingId(null); }
  };

  const copyLink = (portal_url) => {
    navigator.clipboard.writeText(portal_url);
    toast.success("Portal link copied");
  };

  return (
    <div className="space-y-3" data-testid="step-finish">
      <div className="text-xs text-slate-600 leading-relaxed">
        Almost done. Turn on rent reminders (we text renters 3 days before rent is due), review the terms, and hit finish.
      </div>

      <div className="rounded-lg border-2 border-slate-200 divide-y divide-slate-100" data-testid="finish-rentals">
        {rentals.map(r => (
          <div key={r.id} className="p-3 flex items-center gap-3" data-testid={`finish-rental-${r.id}`}>
            <div className="h-8 w-8 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center">
              <House weight="fill" size={14}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-black text-sm truncate">{r.unit_label}</div>
              <div className="text-[11px] text-slate-500 truncate">{r.address}</div>
            </div>
            <button type="button" onClick={() => copyLink(r.portal_url)}
              data-testid={`copy-portal-${r.id}`}
              className="h-8 px-2 rounded-md bg-white border-2 border-slate-200 hover:border-sky-500 text-slate-600 hover:text-sky-600 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
              <CopyIcon size={11}/> Portal
            </button>
            <button type="button" onClick={() => toggleReminder(r)} disabled={savingId === r.id}
              data-testid={`toggle-reminder-${r.id}`}
              className={`h-8 px-2 rounded-md text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-60 ${
                r.rent_reminder_enabled
                  ? "bg-emerald-500 text-white"
                  : "bg-slate-200 text-slate-700"}`}>
              {r.rent_reminder_enabled ? <CheckCircle weight="fill" size={11}/> : <X size={11}/>}
              Reminders {r.rent_reminder_enabled ? "ON" : "OFF"}
            </button>
          </div>
        ))}
      </div>

      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2">
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Terms</div>
        <ul className="text-[11px] text-slate-600 space-y-1 list-disc pl-4">
          <li>Field Radar is a tool — you're the landlord of record and legally responsible for your rentals.</li>
          <li>AI triage drafts messages; nothing sends without your tap unless you enable auto-dispatch.</li>
          <li>Philly L&I lookups pull public records; you're responsible for verifying the license on your rental.</li>
          <li>Landlord-tenant law is state-specific — we surface reminders, we don't give legal advice.</li>
        </ul>
        <label className="flex items-start gap-2 pt-1">
          <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
            data-testid="finish-agree-checkbox"
            className="mt-0.5 h-4 w-4 accent-sky-600"/>
          <span className="text-xs text-slate-700">I understand and want to start using Landlord Autopilot.</span>
        </label>
      </div>

      <button type="button" onClick={onDone} disabled={!agreed}
        data-testid="finish-btn"
        className="w-full h-12 rounded-lg bg-sky-600 hover:bg-sky-700 text-white font-black uppercase text-sm tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
        <Sparkle weight="fill" size={16}/> Finish setup — take me to my dashboard
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Field helper
// ═══════════════════════════════════════════════════════════════════
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}
